import { FC } from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import GenericRowActions, {
  RowAction,
  rowActionsWidth,
} from '../../components/RowActions';
import { HomeDocument } from './types';

// The four per-row actions of the XD documents table: view, edit,
// download and delete, over the common "•••" (components/RowActions).
//
// Edit and delete only appear when `canWrite`: the backend would deny them
// with 403, and what the API is going to reject is not offered.
//
// The `grid` variant is the "Flux 2" document card: the "•••" expands in
// place and the strip carries no "Voir" — the card itself (thumbnail and
// title) opens the record.

export type DocumentAction = 'view' | 'edit' | 'download' | 'delete';

type Props = {
  document: HomeDocument;
  canWrite: boolean;
  /** `table` = hover strip; `card` = menu; `grid` = "•••" expanding in place. */
  variant: 'table' | 'card' | 'grid';
  onAction: (action: DocumentAction, document: HomeDocument) => void;
};

export const DOCUMENT_ACTIONS_WIDTH = rowActionsWidth(4);

const RowActions: FC<Props> = ({ document, canWrite, variant, onAction }) => {
  const view: RowAction<DocumentAction> = {
    key: 'view',
    label: 'Voir',
    icon: <VisibilityOutlinedIcon fontSize="small" />,
  };
  const writeOnly: RowAction<DocumentAction>[] = canWrite
    ? [{ key: 'edit', label: 'Modifier', icon: <EditOutlinedIcon fontSize="small" /> }]
    : [];
  const download: RowAction<DocumentAction> = {
    key: 'download',
    label: 'Télécharger',
    icon: <FileDownloadOutlinedIcon fontSize="small" />,
  };
  const remove: RowAction<DocumentAction>[] = canWrite
    ? [{ key: 'delete', label: 'Supprimer', icon: <DeleteOutlinedIcon fontSize="small" /> }]
    : [];

  const actions: RowAction<DocumentAction>[] =
    variant === 'grid'
      ? [...writeOnly, download, ...remove]
      : [view, ...writeOnly, download, ...remove];

  return (
    <GenericRowActions
      subject={document.title}
      actions={actions}
      variant={variant === 'grid' ? 'expand' : variant}
      width={DOCUMENT_ACTIONS_WIDTH}
      onAction={(action) => onAction(action, document)}
    />
  );
};

export default RowActions;
